<script setup lang="ts">
const props = defineProps<{
    date: string
    type?: string
    bgColor?: string
    increasedFontSize?: boolean
    isSimple?: boolean
}>()

function calculateRemainingTime(dateString: string) {
    const targetDate = new Date(dateString)
    const now = new Date()
    const timeDifference = targetDate.getTime() - now.getTime()

    if (timeDifference <= 0) {
        return { hours: '0', minutes: '0', seconds: '0', daysLeft: '0' }
    }

    const totalSeconds = Math.floor(timeDifference / 1000)
    const days = Math.floor(totalSeconds / (24 * 60 * 60))
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
    const seconds = totalSeconds % 60
    return {
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0'),
        daysLeft: days.toString()
    }
}

const time = ref(calculateRemainingTime(props.date))
const customColor = computed(() => {
    if (props.type === 'A') {
        return `color: ${props.bgColor}`
    }
    return ''
})

const styleCountdownTypeA = 'bg-base-200 px-1 rounded-xs text-base-content'
const countdownStyles = computed(() => {
    return props.type === 'A' ? styleCountdownTypeA : ''
})

let intervalId: ReturnType<typeof setInterval>
onMounted(() => {
    intervalId = setInterval(() => {
        time.value = calculateRemainingTime(props.date)
    }, 1000)
})

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId)
    }
})
</script>

<template>
    <div class="flex text-base-200" :class="[{ 'gap-3': type === 'C' }]">
        <div class="flex justify-center" :class="[isSimple ? 'md:flex-col' : 'flex-col', type === 'C' ? 'items-baseline' : 'items-center', { 'flex-row gap-1': type === 'C' }]">
            <div>
                <span
                    v-if="time.daysLeft.length < 2"
                    class="gap-0.5 flex"
                    :class="{
                        'text-lg' : type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'text-xl font-semibold': type === 'C'
                    }"
                >
                    <span :class="[countdownStyles, { 'hidden md:block' : isSimple }]" :style="customColor">0</span>
                    <span :class="countdownStyles" :style="customColor">
                        {{ time.daysLeft }}
                    </span>
                </span>

                <span
                    v-else
                    class="gap-0.5 flex"
                    :class="{
                        'text-lg' : type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'text-xl font-semibold': type === 'C'
                    }"
                >
                    <span
                        v-for="(digit, index) in time.daysLeft"
                        :key="index"
                        :class="countdownStyles"
                        :style="customColor"
                    >
                        {{ digit }}
                    </span>
                </span>
            </div>
            <div v-if="!isSimple && type !== 'C'" class="text-xs font-semibold">
                {{ $t('misc.countdown.days') }}
            </div>
            <div v-else-if="isSimple" class="font-semibold">
                <span class="md:hidden text-base">{{ $t('misc.countdown.days').slice(0, 1) }}</span>
                <span class="hidden md:block text-xs">{{ $t('misc.countdown.days') }}</span>
            </div>
            <div v-else-if="type === 'C'" class="text-xxs">
                {{ $t('misc.countdown.days') }}
            </div>
        </div>

        <div
            v-if="type !== 'C'"
            class="font-bold"
            :class="{
                'pb-5' : type === 'A',
                'text-lg' : !increasedFontSize,
                'text-base md:text-2xl md:-mt-[20px]': increasedFontSize && type === 'B'
            }"
        >
            :
        </div>

        <div class="flex justify-center" :class="[isSimple ? 'md:flex-col' : 'flex-col', type === 'C' ? 'items-baseline' : 'items-center', { 'flex-row gap-1': type === 'C' }]">
            <div>
                <span
                    v-if="time.hours.length < 2"
                    class="gap-0.5 flex"
                    :class="{
                        'text-lg' : type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'text-xl font-semibold': type === 'C'
                    }"
                >
                    <span :class="[countdownStyles, { 'hidden md:block' : isSimple }]" :style="customColor">0</span>
                    <span :class="countdownStyles" :style="customColor">
                        {{ time.hours }}
                    </span>
                </span>

                <span
                    v-else
                    class="gap-0.5 flex"
                    :class="{
                        'text-lg': type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'text-xl font-semibold': type === 'C'
                    }"
                >
                    <span
                        v-for="(digit, index) in time.hours"
                        :key="index"
                        :class="countdownStyles"
                        :style="customColor"
                    >
                        {{ digit }}
                    </span>
                </span>
            </div>
            <div v-if="!isSimple && type !== 'C'" class="text-xs font-semibold">
                {{ $t('misc.countdown.hours') }}
            </div>
            <div v-else-if="isSimple" class="font-semibold">
                <span class="md:hidden text-base">{{ $t('misc.countdown.hours').slice(0, 1) }}</span>
                <span class="hidden md:block text-xs">{{ $t('misc.countdown.hours') }}</span>
            </div>
            <div v-else-if="type === 'C'" class="text-xxs">
                {{ $t('misc.countdown.hoursShort') }}
            </div>
        </div>

        <div
            v-if="type !== 'C'"
            class="font-bold"
            :class="{
                'pb-5' : type === 'A',
                'text-lg' : !increasedFontSize,
                'text-base md:text-2xl md:-mt-[20px]': increasedFontSize && type === 'B'
            }"
        >
            :
        </div>

        <div class="flex justify-center" :class="[isSimple ? 'md:flex-col' : 'flex-col', type === 'C' ? 'items-baseline' : 'items-center', { 'flex-row gap-1': type === 'C' }]">
            <div>
                <span
                    v-if="time.minutes.length < 2"
                    class="gap-0.5 flex"
                    :class="{
                        'text-lg' : type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'text-xl font-semibold': type === 'C'
                    }"
                >
                    <span :class="[countdownStyles, { 'hidden md:block' : isSimple }]" :style="customColor">0</span>
                    <span :class="countdownStyles" :style="customColor">
                        {{ time.minutes }}
                    </span>
                </span>

                <span
                    v-else
                    :class="{
                        'flex gap-0.5 text-lg' : type === 'A',
                        'text-2xl': !increasedFontSize && type === 'B',
                        'text-base md:text-6xl font-semibold' : increasedFontSize && type === 'B',
                        'flex gap-0.5 text-xl font-semibold': type === 'C'
                    }"
                >
                    <span
                        v-for="(digit, index) in time.minutes"
                        :key="index"
                        :class="countdownStyles"
                        :style="customColor"
                    >
                        {{ digit }}
                    </span>
                </span>
            </div>
            <div v-if="!isSimple && type !== 'C'" class="text-xs font-semibold">
                {{ $t('misc.countdown.minutes') }}
            </div>
            <div v-else-if="isSimple" class="font-semibold">
                <span class="md:hidden text-base">{{ $t('misc.countdown.minutes').slice(0, 1) }}</span>
                <span class="hidden md:block text-xs">{{ $t('misc.countdown.minutes') }}</span>
            </div>
            <div v-else-if="type === 'C'" class="text-xxs">
                {{ $t('misc.countdown.minutesShort') }}
            </div>
        </div>

        <div v-if="type === 'C'" class="flex justify-center items-baseline flex-row gap-1">
            <div>
                <span
                    v-if="time.seconds.length < 2"
                    class="gap-0.5 flex text-xl font-semibold"
                >
                    <span>0</span>
                    <span>
                        {{ time.seconds }}
                    </span>
                </span>

                <span
                    v-else
                    class="flex gap-0.5 text-xl font-semibold"
                >
                    <span
                        v-for="(digit, index) in time.seconds"
                        :key="index"
                    >
                        {{ digit }}
                    </span>
                </span>
            </div>
            <div class="text-xxs">
                {{ $t('misc.countdown.secondsShort') }}
            </div>
        </div>
    </div>
</template>